/**
 * QC coding checks — the rules behind the Coding-checks tab.
 *
 * Completeness: which fields each ball should carry, by the coding app's own
 * rules (e.g. a wagon pin is only expected when the bat met the ball — the
 * same test BallByBallPage uses for `wagonRequired`).
 *
 * Consistency: codes on one ball that contradict each other, or that are
 * unusual enough to be worth a second look. Each flag names its ball so the
 * QC page can open it for editing.
 */
import { inOrder, isAerial, noBatContact } from "./qcData";

// Wickets where the bat never sent the ball anywhere — no wagon pin expected.
const NO_WAGON_WICKETS = new Set([
  "BOWLED", "LBW", "STUMPED", "MANKAD", "RETIRED", "RETIRED_HURT", "RETIRED_OUT", "TIMED_OUT", "HIT_WICKET",
]);

const hasPin = (b) => (b.wx != null && b.wy != null) || !!b.structuredPos;
const wagonExpected = (b) => !b.isWide && !noBatContact(b) && !(b.wicketRaw && NO_WAGON_WICKETS.has(b.wicketRaw));

export const COMPLETENESS_FIELDS = [
  { key: "wagon", label: "Wagon", need: wagonExpected, has: hasPin },
  { key: "length", label: "Length", need: () => true, has: (b) => !!b.length },
  { key: "line", label: "Line", need: () => true, has: (b) => !!b.line },
  { key: "height", label: "Height", need: (b) => b.faced, has: (b) => !!b.height },
  { key: "lineAtBat", label: "Line @ bat", need: (b) => b.faced, has: (b) => !!b.lineAtBat },
  { key: "delivery", label: "Delivery", need: () => true, has: (b) => !!b.delivery },
  { key: "stroke", label: "Stroke", need: (b) => b.faced, has: (b) => !!b.stroke },
  { key: "conn", label: "Connection", need: (b) => b.faced, has: (b) => !!b.conn },
  { key: "foot", label: "Footwork", need: (b) => b.faced, has: (b) => !!b.foot },
  { key: "release", label: "Release", need: () => true, has: (b) => !!b.releasePos },
];

// Distance of a stored wagon pin from the centre, 1 = the rope.
function pinRadius(b) {
  if (b.wx == null || b.wy == null) return null;
  const unit = Math.abs(b.wx) <= 1.5 && Math.abs(b.wy) <= 1.5;
  const c = unit ? 0.5 : 50, h = unit ? 0.5 : 50;
  return Math.hypot((b.wx - c) / h, (b.wy - c) / h);
}

const hasAction = (b, re) => b.fieldings.some((f) => re.test(f.action));

// Per-ball rules. severity: "error" = the codes contradict each other,
// "check" = unusual, worth a look, "missing" = a detail the event needs.
const RULES = [
  { id: "boundary-flag-runs", severity: "error", test: (b) => b.isBoundaryFlag && b.offBat !== 4 && b.offBat !== 6,
    text: (b) => `Boundary flag on, but ${b.offBat} off the bat` },
  { id: "boundary-unflagged", severity: "check", test: (b) => (b.offBat === 4 || b.offBat === 6) && !b.isBoundaryFlag,
    text: (b) => `${b.offBat} off the bat but not flagged as a boundary (all run?)` },
  { id: "boundary-inside-ring", severity: "error", test: (b) => (b.offBat === 4 || b.offBat === 6) && (pinRadius(b) ?? 1) < 0.5,
    text: (b) => `${b.offBat === 6 ? "Six" : "Four"} pinned inside the 30-yard ring` },
  { id: "dot-near-rope", severity: "check", test: (b) => b.faced && b.offBat === 0 && !b.wicket && (pinRadius(b) ?? 0) > 0.85,
    text: () => "Dot ball pinned out near the rope" },
  { id: "runs-no-contact", severity: "error", test: (b) => b.offBat > 0 && noBatContact(b),
    text: (b) => `${b.offBat} off the bat, but coded as no bat contact (${b.conn || b.stroke})` },
  { id: "wide-shot", severity: "error", test: (b) => b.isWide && (hasPin(b) || (b.stroke && !noBatContact(b))),
    text: () => "Wide, but a shot or wagon pin is coded" },
  { id: "bowled-runs", severity: "error", test: (b) => ["BOWLED", "LBW", "STUMPED"].includes(b.wicketRaw) && b.offBat > 0,
    text: (b) => `${b.wicketRaw.toLowerCase()} with ${b.offBat} off the bat` },
  { id: "caught-no-fielder", severity: "missing", test: (b) => /^CAUGHT/.test(b.wicketRaw || "") && !b.caughtBy && !hasAction(b, /^caught$/i),
    text: () => "Caught, but no catcher coded" },
  { id: "stumped-no-keeper", severity: "missing", test: (b) => b.wicketRaw === "STUMPED" && !b.stumpedBy,
    text: () => "Stumped, but no keeper coded" },
  { id: "runout-no-fielder", severity: "missing", test: (b) => b.wicketRaw === "RUN_OUT" && !b.runOutBy && !hasAction(b, /^run out/i),
    text: () => "Run out, but no fielder coded" },
  { id: "wicket-no-batter", severity: "missing", test: (b) => b.wicketRaw && !b.dismissedId,
    text: () => "Wicket with no dismissed batter" },
  { id: "drop-no-fielder", severity: "missing", test: (b) => b.fieldings.some((f) => /^dropped$/i.test(f.action) && !f.player),
    text: () => "Dropped catch with no fielder" },
  { id: "aerial-no-control", severity: "missing", test: (b) => isAerial(b) && !b.control,
    text: (b) => `Shot in the air (${String(b.air).toLowerCase()}) with no control coded` },
  { id: "noball-no-type", severity: "missing", test: (b) => b.isNoBall && !b.noBallType,
    text: () => "No-ball with no no-ball type" },
  { id: "edit-later", severity: "check", test: (b) => b.editLater,
    text: () => "Marked 'edit later'" },
];

// `checkOvers: false` skips the legal-balls-per-over rule — it is only
// meaningful on the whole match, not on a filtered subset of balls.
export function consistencyFlags(balls, { checkOvers = true } = {}) {
  const flags = [];
  for (const b of balls) {
    for (const r of RULES) {
      if (r.test(b)) flags.push({ id: `${r.id}:${b.i}`, rule: r.id, severity: r.severity, text: r.text(b), b });
    }
  }
  // Overs with the wrong number of legal balls — every over but the last
  // (still in progress) of each innings.
  const byInn = new Map();
  for (const b of inOrder(balls)) {
    if (b.innings == null) continue;
    const overs = byInn.get(b.innings) || new Map();
    const o = overs.get(b.over) || { legal: 0, last: b };
    if (b.legal) o.legal += 1;
    o.last = b;
    overs.set(b.over, o);
    byInn.set(b.innings, overs);
  }
  for (const overs of (checkOvers ? byInn.values() : [])) {
    const list = [...overs.entries()].sort((a, b) => a[0] - b[0]);
    list.slice(0, -1).forEach(([over, o]) => {
      if (o.legal !== 6) {
        flags.push({ id: `over:${o.last.innings}:${over}`, rule: "over-count", severity: "error", text: `Over ${over} has ${o.legal} legal balls`, b: o.last });
      }
    });
  }
  const rank = { error: 0, missing: 1, check: 2 };
  return flags.sort((a, c) => rank[a.severity] - rank[c.severity] || (a.b.innings ?? 0) - (c.b.innings ?? 0) || a.b.ballNumber - c.b.ballNumber);
}
